import React, { useState, useEffect } from 'react';
import { UserPlus, Loader, CheckCircle, Sparkles, Mail, Phone } from 'lucide-react';
import { getInterviewers, createInterviewer, getOrganisations } from '../services/api';

function InterviewerList() {
  const [interviewers, setInterviewers] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ full_name: '', email: '', password: '', phone: '', organization_id: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [data, orgData] = await Promise.all([
        getInterviewers(),
        getOrganisations().catch(() => []),
      ]);
      setInterviewers(data || []);
      setOrganisations(orgData || []);
    } catch (e) {
      setError('Failed to load interviewers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const orgName = (id) => organisations.find((o) => o.organisation_id === id)?.name || id || '—';

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.full_name.trim() || !form.email.trim() || !form.password) {
      setError('Name, email and password are required.');
      return;
    }
    setCreating(true);
    try {
      await createInterviewer({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
        phone: form.phone.trim() || null,
        organization_id: form.organization_id || null,
      });
      setSuccess(`Interviewer ${form.email} created.`);
      setForm({ full_name: '', email: '', password: '', phone: '', organization_id: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create interviewer.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-ink-100 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-brand-400" /> Interviewers (Panel)
        </h2>
        <p className="text-sm text-ink-300 mt-1">
          Panelists who conduct human-led interviews with the AI copilot. Assign them to a panel when
          sending a Panel interview; each signs in to their own Interview Copilot dashboard.
        </p>
      </div>

      <div className="bg-ink-800 border border-ink-700 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-ink-100 mb-4 flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-brand-400" /> Add an interviewer
        </h3>
        <form onSubmit={handleCreate} className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-ink-400 mb-1">Full name *</label>
            <input type="text" value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-400 mb-1">Email *</label>
            <input type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-400 mb-1">Temporary password *</label>
            <input type="text" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="They can change it after signing in"
              className="w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-400 mb-1">Phone (optional)</label>
            <input type="text" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-ink-400 mb-1">Organization</label>
            <select value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
              className="w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent">
              <option value="">{organisations.length > 1 ? 'Select organization…' : 'Your organization'}</option>
              {organisations.filter((o) => o.organisation_id).map((o) => (
                <option key={o.organisation_id} value={o.organisation_id}>{o.name}</option>
              ))}
            </select>
          </div>

          {error && <div className="sm:col-span-2 text-sm text-red-400">{error}</div>}
          {success && (
            <div className="sm:col-span-2 text-sm text-green-400 flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4" /> {success}
            </div>
          )}

          <div className="sm:col-span-2">
            <button type="submit" disabled={creating}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-400 transition-colors disabled:opacity-50">
              {creating ? <><Loader className="w-4 h-4 animate-spin" /> Creating…</> : <><UserPlus className="w-4 h-4" /> Create interviewer</>}
            </button>
          </div>
        </form>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-100 mb-3">Existing interviewers</h3>
        {loading ? (
          <p className="text-ink-400 text-sm py-6 text-center">Loading…</p>
        ) : interviewers.length === 0 ? (
          <p className="text-ink-400 text-sm py-6 text-center bg-ink-800 border border-ink-700 rounded-lg">
            No interviewers yet. Create one above.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-800">
              <thead className="bg-ink-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-400 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-400 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-400 uppercase tracking-wider">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-400 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-ink-900 divide-y divide-ink-800">
                {interviewers.map((p) => (
                  <tr key={p.user_id} className="hover:bg-ink-800">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-ink-100">{p.full_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-400">
                      <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {p.email}</span>
                      {p.phone && <span className="flex items-center gap-1.5 mt-0.5"><Phone className="w-3.5 h-3.5" /> {p.phone}</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-400">{orgName(p.organization_id)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        p.is_active === false ? 'bg-red-500/15 text-red-300' : 'bg-green-500/15 text-green-300'
                      }`}>
                        {p.is_active === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default InterviewerList;
